import React, { useCallback } from "react";

import { Stack, TablePagination, Typography } from "@mui/material";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { QuickViewDeleteIcon, QuickViewEditIcon } from "assets/material-custom-icons";
import { Menu } from "components/popups";
import { getClientTypeLabel } from "constants/index";
import { useModals, useNavigate } from "hooks";
import { useClientActions } from "store";
import { IPaginationResponse } from "store/types";
import { IClient } from "store/types/user-management";

const cellTextStyles: TableCellProps["sx"] = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  padding: "0 20px",
  color: "#0a3142",
  fontSize: "16px",
  height: "50px"
};

interface HeadCell {
  key: string;
  title: string;
}

const headCells: HeadCell[] = [
  {
    key: "name",
    title: "Client name"
  },
  {
    key: "type",
    title: "Type"
  },
  {
    key: "rm",
    title: "RM"
  },
  {
    key: "entity_count",
    title: "Entities"
  },
  {
    key: "actions",
    title: ""
  }
];

interface Props {
  refetchTable: () => void;
  handleChangePage: (newPage: number) => void;
  data: IPaginationResponse<IClient>;
  page: number;
  rowsPerPage: number;
  onEditClient: (client: IClient) => void;
}

export const Table: React.FC<Props> = ({ handleChangePage, data, rowsPerPage, page, refetchTable, onEditClient }) => {
  const navigate = useNavigate();
  const { deleteModal } = useModals();
  const { deleteClientAction } = useClientActions();
  const { total, data: rowsData } = data;

  const handleRowClick = useCallback(
    (id: number) => {
      navigate({ route: "clientDashboard", params: { client_id: id } });
    },
    [navigate]
  );

  const onDelete = useCallback(
    (client: IClient) => {
      deleteModal({
        action: async () => {
          await deleteClientAction(client.id);
          refetchTable();
        },
        element: "Delete",
        onPressSuccess: "Confirm",
        message: `Are you sure you want to delete "${client.name}"? This action is irreversible and permanently removes the client and its data.`
      });
    },
    [deleteModal, deleteClientAction, refetchTable]
  );

  return (
    <Stack sx={{ flexGrow: 1, overflow: "hidden", width: "100%" }}>
      <TableContainer
        sx={{
          width: "100%",
          borderRadius: "10px",
          border: "1px solid #dae0e2",
          borderTop: "none",
          paddingBottom: "7px",
          flexGrow: 1,
          backgroundColor: "white"
        }}
      >
        <MuiTable
          sx={{
            width: "100%",
            borderRadius: "10px 20px 30px 40px",
            color: "#0a3142"
          }}
          aria-labelledby="tableTitle"
        >
          <TableHead sx={{ position: "sticky", top: 0, backgroundColor: "white", height: "40px" }}>
            <TableRow sx={{ backgroundColor: "#edf1f9" }}>
              {headCells.map(({ title, key }) => (
                <TableCell
                  sx={{
                    fontSize: "15px",
                    fontWeight: "500",
                    ...cellTextStyles,
                    padding: !title.length ? "0px 5px" : "0px 20px"
                  }}
                  key={key}
                >
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsData.map((row: IClient) => {
              const { id, name, type, rm, entity_count } = row;
              return (
                <TableRow
                  sx={{
                    cursor: "pointer",
                    ":hover": { backgroundColor: "#f5f7f7", ".MuiTableCell-root": { opacity: 1 } }
                  }}
                  key={id}
                  onClick={() => handleRowClick(id)}
                >
                  <TableCell
                    sx={{
                      fontSize: "16px",
                      width: "240px",
                      maxWidth: "240px",
                      ...cellTextStyles,
                      cursor: "pointer",
                      color: "#26A6AE"
                    }}
                    align="left"
                  >
                    {name}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      width: "160px",
                      maxWidth: "160px",
                      ...cellTextStyles
                    }}
                  >
                    {getClientTypeLabel(type) ?? (
                      <Typography component="span" sx={{ color: "#898989" }}>
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      width: "200px",
                      maxWidth: "200px",
                      ...cellTextStyles
                    }}
                  >
                    {rm ?? (
                      <Typography component="span" sx={{ color: "#898989" }}>
                        {"\u2014"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      width: "120px",
                      maxWidth: "120px",
                      color: entity_count === 0 ? "#898989" : "#0a3142",
                      ...cellTextStyles
                    }}
                  >
                    {entity_count}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={(event) => event.stopPropagation()}
                    sx={{ width: "60px", maxWidth: "60px", opacity: 0, ...cellTextStyles, padding: "0px 5px" }}
                  >
                    <Menu
                      options={[
                        {
                          title: "Edit",
                          icon: <QuickViewEditIcon />,
                          onClick: () => onEditClient(row)
                        },
                        {
                          title: "Delete",
                          icon: <QuickViewDeleteIcon />,
                          onClick: () => onDelete(row)
                        }
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <TablePagination
        align="center"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          height: "40px",
          ".MuiTablePagination-toolbar": { minHeight: "40px" }
        }}
        rowsPerPageOptions={[50]}
        component="div"
        rowsPerPage={rowsPerPage}
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => {
          handleChangePage(newPage);
        }}
      />
    </Stack>
  );
};
